/**
 * Client-side contract for user activity events.
 *
 * MIRRORS apps/MJAPI/src/resolvers/activity/events.ts and the GraphQL input
 * `UserActivityEventInput` in apps/MJAPI/src/resolvers/UserActivityResolver.ts.
 * No interfaces-only package is shared between MJAPI and MJExplorer (spec
 * §2.5), so the literal unions here and there must stay identical; the server
 * rejects anything outside them.
 */
export type UserActivityApp = 'MAPS' | 'Research Accelerator' | 'Feedback Platform';
export type UserActivityKind = 'page-view' | 'ui-click';
export type UserActivityElementKind = 'button' | 'link' | 'mj-button' | 'role-button';

/** What a surface knows about "where" for a page view. */
export interface UserActivityPageDescriptor {
    readonly Page: string;
    readonly RecordID: string | null;
}

interface UserActivityEventBase {
    readonly App: UserActivityApp;
    /** MJ: Dashboards row the surface is mounted from — distinguishes the four RA compositions. */
    readonly DashboardID: string;
    /** Per-tab UUID minted once per UserActivityService instance; lets time-on-page be derived later. */
    readonly SessionID: string;
    readonly Page: string;
    readonly RecordID: string | null;
    /** ISO timestamp taken at capture time, not flush time. */
    readonly OccurredAt: string;
}

export interface UserActivityPageView extends UserActivityEventBase {
    readonly Kind: 'page-view';
}

export interface UserActivityClick extends UserActivityEventBase {
    readonly Kind: 'ui-click';
    /** ≤ CLICK_LABEL_MAX characters, see click-label.ts. */
    readonly Label: string;
    readonly ElementKind: UserActivityElementKind;
    /** Anchors only. */
    readonly Href: string | null;
}

export type UserActivityEvent = UserActivityPageView | UserActivityClick;

/** The GraphQL `UserActivityEventInput` shape, field for field. */
export interface UserActivityEventInput {
    readonly kind: UserActivityKind;
    readonly app: UserActivityApp;
    readonly dashboardID: string;
    readonly sessionID: string;
    readonly page: string;
    readonly recordID: string | null;
    readonly occurredAt: string;
    readonly label: string | null;
    readonly elementKind: UserActivityElementKind | null;
    readonly href: string | null;
}

export function ToUserActivityEventInput(event: UserActivityEvent): UserActivityEventInput {
    return {
        kind: event.Kind,
        app: event.App,
        dashboardID: event.DashboardID,
        sessionID: event.SessionID,
        page: event.Page,
        recordID: event.RecordID,
        occurredAt: event.OccurredAt,
        label: event.Kind === 'ui-click' ? event.Label : null,
        elementKind: event.Kind === 'ui-click' ? event.ElementKind : null,
        href: event.Kind === 'ui-click' ? event.Href : null,
    };
}

/**
 * The surface's identity comes from the MJ: Dashboards row it is mounted from
 * (`BaseDashboard.Config.dashboard.DriverClass`), which is exactly the string
 * each surface registers with @RegisterClass(BaseDashboard, ...). All four
 * Research Accelerator compositions share 'AGUAppSurface'; DashboardID tells
 * them apart.
 */
const APP_BY_DRIVER_CLASS: Readonly<Record<string, UserActivityApp>> = {
    MAPSAppSurface: 'MAPS',
    AGUAppSurface: 'Research Accelerator',
    FeedbackPlatformAppSurface: 'Feedback Platform',
};

export function AppFromDriverClass(driverClass: string | null | undefined): UserActivityApp | null {
    if (!driverClass) return null;
    return APP_BY_DRIVER_CLASS[driverClass] ?? null;
}
